import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Typography,
    Box
} from '@mui/material';
import { designTokens } from '../../theme/designTokens';

/**
 * Props for the ConfirmDialog component
 */
interface ConfirmDialogProps {
    /** Whether the dialog is open */
    open: boolean;
    /** Function to call when the dialog is closed (e.g., via backdrop click or cancel) */
    onClose?: () => void;
    /** Function to call when the user confirms the action */
    onConfirm: () => void;
    /** Title of the dialog */
    title: string;
    /** Content text of the dialog */
    content: string;
    /** Label for the cancel button. Defaults to "Annuler" */
    cancelLabel?: string;
    /** Label for the confirm button. Defaults to "Confirmer" */
    confirmLabel?: string;
    /** Color of the confirm button. Defaults to "primary" */
    confirmColor?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    /** Whether the action is destructive (e.g. delete), styling the confirm button as error */
    isDestructive?: boolean;
    /** Function to call when the cancel button is clicked. If not provided, onClose is used. */
    onCancel?: () => void;
}

/**
 * A reusable confirmation dialog component.
 *
 * @param props - The props for the component
 * @returns A Dialog component with title, content, and action buttons
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Item"
 *   content="Are you sure you want to delete this item?"
 *   confirmColor="error"
 * />
 * ```
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    onClose,
    onConfirm,
    title,
    content,
    cancelLabel = 'Annuler',
    confirmLabel = 'Confirmer',
    confirmColor = 'primary',
    isDestructive = false,
    onCancel
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose || onCancel}
            PaperProps={{
                sx: {
                    ...designTokens.glassCard,
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    minWidth: '300px'
                }
            }}
        >
            <DialogTitle sx={{ color: '#fff' }}>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {content}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel || onClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {cancelLabel}
                </Button>
                <Button
                    onClick={onConfirm}
                    color={isDestructive ? 'error' : confirmColor}
                    variant="contained"
                    autoFocus
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDialog;
